import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Dropdown, Nav, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import Swal from "sweetalert2";
import TreeInfo from "./TreeInfo";
import MoistureGauge from "./MoistureGauge";
import Tree from "./Tree";
import TreeModelViewer from "./TreeModelViewer";
import TreeDetailCareChat from "./TreeDetailCareChat";
import TreeDetailCareComposerInline from "./TreeDetailCareComposerInline";
import InfoBox from "@components/InfoBox/InfoBox";
import {
  WrenchIcon,
  ClockIcon,
  AddIcon,
  AngleLeftIcon,
  AngleRightIcon,
  TrashIcon,
  MoreIcon,
  EditIcon,
  InfoIcon,
  CloseIcon,
  ClipboardDataIcon,
  MapMarkerAltIcon,
} from "@components/icons";
import { statusList } from "@helper/healthState";
import { colorsDendrometer, findClosest, GREEN, YELLOW, RED } from "@helper/colors";
import { parentTreeIcon } from "@helper/marker";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getAllTasks,
  createTask,
  updateTask,
  deleteTask,
  uploadTaskRegelkontrolleDocument,
} from "@modules/Tasks/services/taskCrud";
import { getLastSend } from "@modules/Sensors/services/sensorCrud";
import ReactTable from "@components/ReactTable";
import { updateInstallation } from "../services/treeCrud";
import InstallationForm from "./Form/AddForm/steps/Installation/InstallationForm";
import HealthThresholdSlider from "./Form/AddForm/steps/HTs";
import SoilThresholdSlider from "./Form/AddForm/steps/SoilThresholds";
import { useBackgroundTreeSpecies } from "@hooks/useBackgroundData";
import "./TreeDetail.css";

const CROWN_SENSOR_TYPES = ["pulse", "dendrometer"];
const SOIL_SENSOR_TYPES = ["soil_moisture", "soil_water_tension"];

const toDateInputValue = (value) => {
  if (!value) return "";
  const stringValue = String(value);
  return stringValue.includes("T") ? stringValue.split("T")[0] : stringValue.slice(0, 10);
};

const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

function SensorMeasurementsTable({ rows, sensorType, valueKey }) {
  const { t, i18n } = useTranslation();
  const timeString =
    i18n.language === "de"
      ? "de-DE"
      : i18n.language === "en"
      ? "en-UK"
      : i18n.language === "it"
      ? "it-IT"
      : "de-DE";

  const columns = useMemo(() => {
    const cols = [
      {
        Header: t("time"),
        accessor: "_time",
        Cell: ({ value }) =>
          new Date(value).toLocaleString(timeString, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
      },
      {
        Header: valueKey ? t(valueKey) : t("resistance"),
        accessor: valueKey || "resistance",
        Cell: ({ value }) => {
          if (value === undefined || value === null) return null;
          const roundedValue = Number(value).toFixed(1);
          const unit =
            sensorType === "pulse"
              ? "kΩ"
              : sensorType === "soil_moisture"
              ? "%"
              : sensorType === "soil_water_tension"
              ? "kPa"
              : sensorType === "dendrometer"
              ? "µm"
              : "";
          return `${roundedValue} ${unit}`;
        },
      },
    ];

    if (sensorType !== "dendrometer") {
      cols.push({
        Header: t("temperature"),
        accessor: "temperature",
        Cell: ({ value }) => {
          if (value === undefined || value === null) return null;
          return `${Number(value).toFixed(1)} °C`;
        },
      });
    }

    cols.push({
      Header: t("battery"),
      accessor: "battery",
      Cell: ({ value }) => {
        if (value === undefined || value === null) return null;
        return `${Number(value).toFixed(3)} V`;
      },
    });

    return cols;
  }, [i18n.language, sensorType, t, timeString, valueKey]);

  return (
    <ReactTable
      columns={columns}
      data={rows}
      activeId={0}
      pagesize={10}
      size={[10, 20, 30, 40, 50]}
      pagination={false}
      filter={false}
    />
  );
}

function statePulse(tree, sensor) {
  const moistureContent = sensor?.pulse_sensor?.moisture_content ?? 0;
  const frost = sensor?.pulse_sensor?.frost ?? false;
  if (sensor?.sensor_state !== 0) return 3;
  if (frost) return 4;
  if (moistureContent > tree.blue_threshold) return 5;
  if (moistureContent > tree.green_threshold) return 0;
  if (moistureContent > tree.yellow_threshold) return 1;
  return 2;
}

function stateSoilMoisture(tree, sensor) {
  const soil = tree.soil;
  const yellowThreshold = soil ? soil.pwp : 10;
  const greenThreshold = soil
    ? soil.pwp + ((soil.max_nf / 2) * (soil.max_nf - soil.pwp)) / soil.max_nf
    : 25;
  const blueThreshold = soil ? soil.max_nf : 45;
  const value = sensor?.soil_moisture_sensor?.soil_moisture || 0;
  if (sensor?.sensor_state !== 0) return 3;
  if (value > blueThreshold) return 5;
  if (value > greenThreshold) return 0;
  if (value > yellowThreshold) return 1;
  return 2;
}

function stateSoilWaterTension(tree, sensor) {
  const soil = tree.soil;
  const yellowThreshold = soil
    ? soil.yellow_threshold_swt
      ? -Math.pow(10, soil.yellow_threshold_swt) / 10
      : -120
    : -120;
  const greenThreshold = soil
    ? soil.green_threshold_swt
      ? -Math.pow(10, soil.green_threshold_swt) / 10
      : -60
    : -60;
  const blueThreshold = soil
    ? soil.blue_threshold_swt
      ? -Math.pow(10, soil.blue_threshold_swt) / 10
      : -6
    : -6;
  const value = sensor?.soil_water_tension_sensor?.soil_water_tension || 0;
  if (sensor?.sensor_state !== 0) return 3;
  if (value > blueThreshold) return 5;
  if (value > greenThreshold) return 0;
  if (value > yellowThreshold) return 1;
  return 2;
}

function stateDendrometer(sensor) {
  if (!sensor || sensor.sensor_state !== 0) {
    return 3;
  }
  const droughtDays = Number(sensor?.dendrometer_sensor?.drought_days);
  if (!Number.isFinite(droughtDays)) {
    return 3;
  }
  const normalizedDays = Math.max(0, Math.min(14, droughtDays)) / 14;
  const [, color] = findClosest(normalizedDays, colorsDendrometer());
  if (color === GREEN) {
    return 0;
  }
  if (color === YELLOW) {
    return 1;
  }
  if (color === RED) {
    return 2;
  }
  return 3;
}

const TASK_STATUSES = ["GEPLANT", "BEAUFTRAGT", "IN_ARBEIT"];
const CARE_STATUS_TO_TASK_STATUS = {
  GEPLANT: "planned",
  BEAUFTRAGT: "review",
  IN_ARBEIT: "in-progress",
  ERLEDIGT: "completed",
  NICHT_GESCHAFFT: "completed",
};

const TASK_STATUS_TO_CARE_STATUS = {
  planned: "GEPLANT",
  review: "BEAUFTRAGT",
  "in-progress": "IN_ARBEIT",
  completed: "ERLEDIGT",
  done: "ERLEDIGT",
};

const CARE_PRIORITY_TO_TASK_PRIORITY = {
  NIEDRIG: "low",
  MITTEL: "medium",
  HOCH: "high",
  KRITISCH: "high",
};
function resolveTimestamp(entry) {
  return entry?.timestamp || entry?.createdAt || entry?.date || null;
}

function normalizeCareEntry(entry) {
  const now = new Date();
  const timestampValue = resolveTimestamp(entry);
  const timestamp = timestampValue || new Date().toISOString();
  let status = entry?.status;

  // Map backend status to frontend status
  if (status && TASK_STATUS_TO_CARE_STATUS[status]) {
    status = TASK_STATUS_TO_CARE_STATUS[status];
  }

  // Set default status if not provided
  if (!status) {
    status = timestampValue
      ? new Date(timestampValue) <= now
        ? "ERLEDIGT"
        : "GEPLANT"
      : "GEPLANT";
  }

  return {
    ...entry,
    timestamp,
    status,
    priority: entry?.priority || "MITTEL",
    data: entry?.data || {},
    assignedTo: entry?.assignedTo || "",
    costCenter: entry?.costCenter || "",
    tags: entry?.tags || [],
    attachments: entry?.attachments || [],
    author: entry?.author || "Du",
    future: timestampValue ? new Date(timestamp) > now : false,
  };
}

function mapCareStatus(status) {
  return CARE_STATUS_TO_TASK_STATUS[status] || status;
}

function mapCarePriority(priority) {
  return CARE_PRIORITY_TO_TASK_PRIORITY[priority] || priority;
}

function mapTaskToCareEntry(task) {
  const assigneeName =
    task.assignee?.name ||
    [task.assignee?.first_name, task.assignee?.last_name].filter(Boolean).join(" ") ||
    task.data?.assigned_to ||
    "";
  const mapped = {
    id: `task-${task.id}`,
    source: "task",
    sourceId: task.id,
    originalTask: task,
    treeId: task.treeId || task.tree?.id,
    measureType: task.measureType || task.title || "Aufgabe",
    status: task.status,
    priority: task.priority,
    timestamp: task.scheduledAt,
    assignedTo: assigneeName,
    costCenter: task.costCenter || "",
    author: task.createdBy?.name || "Aufgabe",
    data: task.data || {},
  };
  return normalizeCareEntry(mapped);
}

function mapImageToCareEntry(image, imageType) {
  const mapped = {
    id: `image-${imageType}-${image.id}`,
    source: "image",
    sourceId: image.id,
    imageType: imageType, // "tree" or "leaf"
    imageUrl: imageType === "tree" ? image.tree_image : image.leaf_image,
    measureType: imageType === "tree" ? "Baumfoto" : "Blattfoto",
    status: "ERLEDIGT",
    priority: "MITTEL",
    timestamp: image.timestamp || image.created_at || new Date().toISOString(),
    assignedTo: "",
    costCenter: "",
    author: "System",
    data: { imageData: image },
  };
  return normalizeCareEntry(mapped);
}

function buildTaskUpdateFromCare(entry, updates) {
  const nextTitle = updates.measureType || entry.measureType;
  const nextAssignedTo = updates.assignedTo ?? entry.assignedTo;
  const nextData = updates.data ? { ...entry.data, ...updates.data } : entry.data;
  if (nextAssignedTo) {
    nextData.assigned_to = nextAssignedTo;
  }
  return {
    id: entry.sourceId,
    title: nextTitle,
    measureType: nextTitle,
    costCenter: updates.costCenter ?? entry.costCenter ?? "",
    scheduledAt: updates.timestamp || entry.timestamp,
    status: mapCareStatus(updates.status || entry.status),
    priority: mapCarePriority(updates.priority || entry.priority),
    data: nextData || {},
  };
}

function buildTaskPayloadFromEntry(entry, treeId) {
  const resolvedTreeId = entry.treeId || treeId;
  const mappedStatus = entry.status || "GEPLANT";
  const mappedPriority = entry.priority || "MITTEL";
  const nextData = { ...(entry.data || {}) };
  if (entry.assignedTo) {
    nextData.assigned_to = entry.assignedTo;
  }
  const payload = {
    title: entry.measureType || "Aufgabe",
    measureType: entry.measureType,
    costCenter: entry.costCenter,
    scheduledAt: entry.timestamp,
    status: mapCareStatus(mappedStatus),
    priority: mapCarePriority(mappedPriority),
    treeId: resolvedTreeId ? Number(resolvedTreeId) : null,
    data: nextData,
    assigneeId: entry.assigneeId,
  };
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === null || payload[key] === "") {
      delete payload[key];
    }
  });
  return payload;
}

function getEntryDocumentFile(entry) {
  return (
    entry?.data?.regelkontrolleDocument?.file || entry?.data?.freieEintragungDocument?.file || null
  );
}

function stripTaskPayloadDocumentFile(payload) {
  if (!payload?.data) return payload;
  const nextData = { ...payload.data };
  const stripDocField = (key) => {
    if (!nextData[key] || typeof nextData[key] !== "object") return;
    const { file, dataUrl, ...restDocumentData } = nextData[key];
    if (Object.keys(restDocumentData).length > 0) {
      nextData[key] = restDocumentData;
    } else {
      delete nextData[key];
    }
  };
  stripDocField("regelkontrolleDocument");
  stripDocField("freieEintragungDocument");
  return { ...payload, data: nextData };
}

export default function TreeDetailTabs({
  tree,
  treeImages,
  leafImages,
  treeIndex,
  setTreeIndex,
  leafIndex,
  setLeafIndex,
  handleDeleteImage,
  setOpenImageForm,
  edit,
  setEdit,
  soilEdit,
  setSoilEdit,
  selectedInstallation,
  setSelectedInstallation,
  showData,
  setShowData,
  onAddSensor,
  onClose,
  headerActions,
  onSensorEditOpenChange,
  canEditTree = true,
  canManage3DModel = false,
  onUpload3DModel,
  onRemove3DModel,
  is3DModelMutating = false,
  loadCareData = false,
  onActiveTabChange,
  showLocationMap = true,
  isPublic = false,
}) {
  const { t } = useTranslation();
  const { treeSpecies } = useBackgroundTreeSpecies();
  const calibrationSpeciesName = useMemo(() => {
    const speciesId = tree?.species_id ?? tree?.species?.id;
    if (!speciesId) return null;
    const entry = treeSpecies.find((sp) => sp.id === speciesId);
    const calibId = entry?.calibration_species;
    if (!calibId) return null;
    return calibId
      .split("_")
      .map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
      .join(" ");
  }, [tree?.species_id, tree?.species?.id, treeSpecies]);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("info");
  const [careEntries, setCareEntries] = useState([]);
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const [composerPrefill, setComposerPrefill] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const infoTabLabel = t("info");
  const sensorOverviewTabLabel = t("evaluation", { defaultValue: "Auswertung" });
  const treeLat = Number(tree?.position?.lat);
  const treeLng = Number(tree?.position?.lng);
  const hasTreePosition = Number.isFinite(treeLat) && Number.isFinite(treeLng);
  const [installIsUpdate, setInstallIsUpdate] = useState(false);
  const [installationItem, setInstallationItem] = useState(null);
  const [sensorHeight, setSensorHeight] = useState(null);
  const [sensorHeightInput, setSensorHeightInput] = useState("");
  const [sensorInstallOpen, setSensorInstallOpen] = useState(false);
  const [sensorDepth, setSensorDepth] = useState(30);
  const [installSensorType, setInstallSensorType] = useState(null);
  const [configTab, setConfigTab] = useState("install");
  const [configScope, setConfigScope] = useState("crown");
  const [show3DViewer, setShow3DViewer] = useState(false);
  const has3DModel = Boolean(tree?.three_d_model_url);
  const [soilWaterTensionType, setSoilWaterTensionType] = useState("kPa");
  const [showInstallationInfo, setShowInstallationInfo] = useState(false);
  const [sensorDetailTab, setSensorDetailTab] = useState("overview");
  const modelFileInputRef = useRef(null);
  const [isEditingInstallationMeta, setIsEditingInstallationMeta] = useState(false);
  const [installationMetaForm, setInstallationMetaForm] = useState({
    installed_at: "",
    removed_at: "",
    height: "",
    depth: "",
  });
  const diagramRef = useRef(null);
  const [diagramWidth, setDiagramWidth] = useState(null);

  const activeInstallations = useMemo(() => {
    const list = Array.isArray(tree?.installations) ? tree.installations : [];
    return list.filter((i) => !i?.removed_at);
  }, [tree?.installations]);

  useEffect(() => {
    if (!has3DModel && show3DViewer) {
      setShow3DViewer(false);
    }
  }, [has3DModel, show3DViewer]);

  const getSensorType = useCallback((sensor) => {
    if (!sensor) return null;
    if (sensor.dendrometer_sensor) return "dendrometer";
    if (sensor.soil_moisture_sensor) return "soil_moisture";
    if (sensor.soil_water_tension_sensor) return "soil_water_tension";
    if (sensor.pulse_sensor) return "pulse";
    const lowerName = sensor.name ? sensor.name.toLowerCase() : "";
    if (lowerName.includes("dendro")) return "dendrometer";
    if (lowerName.includes("tensi") || lowerName.includes("tension")) return "soil_water_tension";
    if (lowerName.includes("moist")) return "soil_moisture";
    if (sensor.sensor_type) return sensor.sensor_type;
    return null;
  }, []);

  const treeStateDescription = useCallback(
    (sensor) => {
      if (!sensor) return "-";
      const sensorType = getSensorType(sensor);
      let state = 3;
      if (sensorType === "pulse") state = statePulse(tree, sensor);
      else if (sensorType === "soil_moisture") state = stateSoilMoisture(tree, sensor);
      else if (sensorType === "soil_water_tension") state = stateSoilWaterTension(tree, sensor);
      else if (sensorType === "dendrometer") state = stateDendrometer(sensor);
      const stateMeta = statusList[state];
      if (!stateMeta) return "-";
      return `${t(stateMeta.label)}: ${t(stateMeta.todo)}`;
    },
    [getSensorType, t, tree]
  );

  const allowedSensorTypes = configScope === "soil" ? SOIL_SENSOR_TYPES : CROWN_SENSOR_TYPES;

  const removeInstallationMutation = useMutation(updateInstallation, {
    onSuccess: (updated) => {
      const removedAt = updated?.removed_at || new Date().toISOString();
      const installationId = updated?.id;
      const patchInstallations = (list = []) =>
        list.map((inst) =>
          inst.id === installationId ? { ...inst, removed_at: removedAt } : inst
        );

      // Update detail cache — still has full installations array
      queryClient.setQueryData(["tree", tree?.id], (prev) =>
        prev ? { ...prev, installations: patchInstallations(prev.installations || []) } : prev
      );

      // Update list cache — uses lightweight fields, no installations array
      const remaining = activeInstallations.filter((i) => i.id !== installationId);
      const newHasActiveSensor = remaining.length > 0;
      const newActiveSensorState = remaining[0]?.sensor?.sensor_state ?? null;
      queryClient.setQueryData("trees", (prev) => {
        if (!prev) return prev;
        const patch = (t) =>
          t.id === tree?.id
            ? {
                ...t,
                has_active_sensor: newHasActiveSensor,
                active_sensor_state: newActiveSensorState,
              }
            : t;
        if (Array.isArray(prev)) return prev.map(patch);
        if (prev.results) return { ...prev, results: prev.results.map(patch) };
        return prev;
      });

      queryClient.invalidateQueries(["tree", tree?.id]);
      queryClient.invalidateQueries("trees");
    },
  });
  const updateInstallationMetaMutation = useMutation(updateInstallation, {
    onSuccess: (updated) => {
      setSelectedInstallation((prev) =>
        prev?.id === updated?.id ? { ...prev, ...updated } : prev
      );
      queryClient.invalidateQueries(["tree", tree?.id]);
      queryClient.invalidateQueries("trees");
    },
  });

  const heightMax = Math.max(1, Math.round(Number(tree?.height) || 30));
  const defaultHeight = Math.max(1, Math.round(heightMax * 0.6));

  const treeForDiagram = useMemo(() => {
    if (!tree) return tree;
    return { ...tree, installations: activeInstallations };
  }, [tree, activeInstallations]);

  useEffect(() => {
    setSensorHeight(defaultHeight);
    setSensorHeightInput(String(defaultHeight));
  }, [defaultHeight, tree?.id]);

  // Keep preview height sensible per sensor type
  useEffect(() => {
    if (!installSensorType) {
      return;
    }
    if (installSensorType === "soil_moisture" || installSensorType === "soil_water_tension") {
      setSensorHeight(0);
      setSensorHeightInput("0");
      setSensorDepth((prev) => (prev == null ? 30 : prev));
      return;
    }
    setSensorHeight((prev) => (prev == null ? defaultHeight : prev));
    setSensorHeightInput((prev) => (prev === "" || prev == null ? String(defaultHeight) : prev));
  }, [installSensorType, defaultHeight]);

  useEffect(() => {
    if (!installSensorType) return;
    if (allowedSensorTypes.includes(installSensorType)) return;
    setInstallSensorType(null);
  }, [installSensorType, allowedSensorTypes]);

  const normalizeHeightValue = (value) => {
    const raw = String(value ?? "").replace(",", ".");
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return null;
    const rounded = Math.round(parsed);
    return Math.min(heightMax, Math.max(1, rounded));
  };

  const commitHeight = (value) => {
    const normalized = normalizeHeightValue(value);
    if (normalized == null) return;
    setSensorHeight(normalized);
    setSensorHeightInput(String(normalized));
  };

  const handlePreviewHeightChange = (nextHeight) => {
    commitHeight(nextHeight);
  };

  const handlePreviewDepthChange = (nextDepth) => {
    const normalizedDepth = Number(nextDepth);
    if (!Number.isFinite(normalizedDepth)) return;
    const clampedDepth = [0, 30, 60, 90, 120].includes(normalizedDepth) ? normalizedDepth : 30;
    setSensorDepth(clampedDepth);
  };

  const openConfigPanel = (tab = "install", scope = "crown") => {
    if (!canEditTree) return;
    onSensorEditOpenChange?.(true);
    setSelectedInstallation(null);
    setInstallIsUpdate(false);
    setInstallationItem(null);
    setConfigTab(tab);
    setConfigScope(scope);
    if (tab === "install") {
      setInstallSensorType(null);
      setSensorDepth(30);
    }
    setSensorInstallOpen(true);
  };

  const handleOpenThresholds = (scope = "crown") => openConfigPanel("thresholds", scope);

  const closeEditor = useCallback(() => {
    onSensorEditOpenChange?.(false);
    setSensorInstallOpen(false);
    setSelectedInstallation(null);
    setInstallSensorType(null);
    setSensorDepth(30);
    setConfigScope("crown");
    setShowInstallationInfo(false);
    setIsEditingInstallationMeta(false);
  }, [onSensorEditOpenChange, setSelectedInstallation]);

  const handleSelectInstallation = (installation) => {
    if (!installation) {
      onSensorEditOpenChange?.(false);
      setSelectedInstallation(null);
      return;
    }
    // Open map edit-focus first so the gauge mounts with the final width.
    onSensorEditOpenChange?.(true);
    setSensorInstallOpen(false);
    requestAnimationFrame(() => {
      const nextWidth = diagramRef.current?.getBoundingClientRect?.().width;
      if (Number.isFinite(nextWidth)) {
        setDiagramWidth(Math.round(nextWidth));
      }
      setSelectedInstallation(installation);
    });
  };
  const handleRemoveInstallation = async () => {
    if (!selectedInstallation?.id || removeInstallationMutation.isLoading) return;
    const result = await Swal.fire({
      title: t("confirm_delete"),
      text: `${t("sensor")}: ${selectedInstallation?.sensor?.name || ""}`,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: "#EF3600",
      cancelButtonColor: "#286CA5",
      cancelButtonText: t("cancel"),
      confirmButtonText: t("delete"),
    });
    if (!result.isConfirmed) return;
    const removed_at = new Date().toISOString().split("T")[0];
    try {
      await removeInstallationMutation.mutateAsync({
        id: selectedInstallation.id,
        formdata: { removed_at },
      });
      closeEditor();
    } catch (err) {
      console.error("Failed to remove installation", err);
    }
  };

  const handleEditInstallation = () => {
    if (!selectedInstallation) return;
    setShowInstallationInfo(true);
    setIsEditingInstallationMeta(true);
  };

  const handleOpenInstallationInfo = () => {
    if (!selectedInstallation) return;
    setShowInstallationInfo(true);
    setIsEditingInstallationMeta(false);
  };

  const handleSaveInstallationMeta = async () => {
    if (!selectedInstallation?.id || updateInstallationMetaMutation.isLoading) return;
    const selectedType = getSensorType(selectedInstallation?.sensor);
    const isSoilType = selectedType === "soil_moisture" || selectedType === "soil_water_tension";
    const payload = {
      installed_at: installationMetaForm.installed_at || null,
      removed_at: installationMetaForm.removed_at || null,
    };
    if (isSoilType) {
      const depthValue = Number(installationMetaForm.depth);
      if (Number.isFinite(depthValue)) {
        payload.depth = depthValue;
      }
    } else {
      const heightValue = Number(String(installationMetaForm.height).replace(",", "."));
      if (Number.isFinite(heightValue)) {
        payload.height = Math.round(heightValue);
      }
    }
    try {
      await updateInstallationMetaMutation.mutateAsync({
        id: selectedInstallation.id,
        formdata: payload,
      });
      setIsEditingInstallationMeta(false);
    } catch (err) {
      console.error("Failed to update installation metadata", err);
    }
  };

  const showSensorPanel = sensorInstallOpen || Boolean(selectedInstallation);
  const showSensorDetails = Boolean(selectedInstallation) && !sensorInstallOpen;
  const selectedInstallationSensorType = getSensorType(selectedInstallation?.sensor);
  const isSelectedInstallationSoilType =
    selectedInstallationSensorType === "soil_moisture" ||
    selectedInstallationSensorType === "soil_water_tension";
  const isSoilInstallType = SOIL_SENSOR_TYPES.includes(installSensorType);
  const hasSelectedInstallSensor = Boolean(installSensorType);
  const showInstallPreview =
    sensorInstallOpen && configTab === "install" && hasSelectedInstallSensor;
  const isStackedEditorLayout = diagramWidth != null ? diagramWidth < 820 : false;
  const editorLayoutKey = isStackedEditorLayout ? "stacked" : "split";
  const thresholdTabLabel =
    configScope === "soil" ? t("adjust_thresholds_soil") : t("adjust_thresholds_crown");

  const { data: selectedInstallationLastSendData, isLoading: isLoadingInstallationMeasurements } =
    useQuery(
      ["tree-installation-last-send", selectedInstallation?.sensor?.id],
      () => getLastSend(selectedInstallation.sensor.id),
      {
        enabled: Boolean(selectedInstallation?.sensor?.id),
        refetchInterval: 60000,
        refetchOnWindowFocus: false,
        retry: 1,
      }
    );

  const installationMeasurements = useMemo(() => {
    const rows = Array.isArray(selectedInstallationLastSendData?.data)
      ? selectedInstallationLastSendData.data
      : [];
    if (!rows.length) return [];

    const installedAtTs = toTimestamp(selectedInstallation?.installed_at);
    const removedAtTs = toTimestamp(selectedInstallation?.removed_at);

    return rows
      .filter((row) => {
        const rowTs = toTimestamp(row?._time);
        if (rowTs == null) return false;
        if (installedAtTs != null && rowTs < installedAtTs) return false;
        if (removedAtTs != null && rowTs > removedAtTs) return false;
        return true;
      })
      .sort((a, b) => toTimestamp(b?._time) - toTimestamp(a?._time))
      .slice(0, 10);
  }, [
    selectedInstallation?.installed_at,
    selectedInstallation?.removed_at,
    selectedInstallationLastSendData,
  ]);

  const installationMeasurementSensorType = selectedInstallationLastSendData?.sensor ?? "";
  const installationMeasurementValueKey = selectedInstallationLastSendData?.value ?? "";

  useEffect(() => {
    if (!selectedInstallation) {
      setInstallationMetaForm({ installed_at: "", removed_at: "", height: "", depth: "" });
      setShowInstallationInfo(false);
      setIsEditingInstallationMeta(false);
      setSensorDetailTab("overview");
      return;
    }
    setSensorDetailTab("overview");
    setInstallationMetaForm({
      installed_at: toDateInputValue(selectedInstallation.installed_at),
      removed_at: toDateInputValue(selectedInstallation.removed_at),
      height:
        selectedInstallation.height === 0 || selectedInstallation.height
          ? String(selectedInstallation.height)
          : "",
      depth:
        selectedInstallation.depth === 0 || selectedInstallation.depth
          ? String(selectedInstallation.depth)
          : "",
    });
  }, [selectedInstallation]);
  const treeInfoHelpContent = (
    <>
      <p>
        <strong>{t("tree_info_help.heading")}</strong>
      </p>

      <p>{t("tree_info_help.description")}</p>

      <p>
        <strong>{t("tree_info_help.fields_heading")}</strong>
      </p>
      <ul>
        <li>
          <strong>{t("tree_info_help.fields.species_label")}</strong>{" "}
          {t("tree_info_help.fields.species_help")}
        </li>
        <li>
          <strong>{t("tree_info_help.fields.dimensions_label")}</strong>{" "}
          {t("tree_info_help.fields.dimensions_help")}
        </li>
        <li>
          <strong>{t("tree_info_help.fields.year_phase_label")}</strong>{" "}
          {t("tree_info_help.fields.year_phase_help")}
        </li>
        <li>
          <strong>{t("tree_info_help.fields.location_label")}</strong>{" "}
          {t("tree_info_help.fields.location_help")}
        </li>
        <li>
          <strong>{t("tree_info_help.fields.status_label")}</strong>{" "}
          {t("tree_info_help.fields.status_help")}
        </li>
      </ul>

      <p>
        <em>{t("tree_info_help.note")}</em>
      </p>
    </>
  );

  const getHealthStateCrown = (sensor) => {
    if (!sensor || [2, 3, 4].includes(sensor.sensor_state)) {
      return 3;
    }
    const moistureContent = sensor?.pulse_sensor?.moisture_content;
    if (moistureContent > tree.blue_threshold) {
      return 5;
    }
    if (moistureContent > tree.green_threshold) {
      return 0;
    }
    if (moistureContent > tree.yellow_threshold) {
      return 1;
    }
    return 2;
  };

  const { data: taskData } = useQuery(
    ["tasks", tree?.id],
    () => getAllTasks(tree?.id ? { treeId: tree.id } : {}),
    {
      enabled: Boolean(tree?.id) && loadCareData && !(tree?.tasks && tree.tasks.length),
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const updateTaskMutation = useMutation(updateTask);
  const deleteTaskMutation = useMutation(deleteTask);

  useEffect(() => {
    if (!tree?.id) return;
    setCareEntries([]);
    setActiveTab("info");
    setPendingScrollId(null);
    setComposerPrefill(null);
    setComposerOpen(false);
    setSensorInstallOpen(false);
    setSelectedInstallation(null);
    setInstallSensorType(null);
    setSensorDepth(30);
    setConfigTab("install");
    setConfigScope("crown");
  }, [tree?.id, setSelectedInstallation]);

  useEffect(() => {
    const node = diagramRef.current;
    if (!node) return;

    const updateWidth = (nextWidth) => {
      if (!Number.isFinite(nextWidth)) return;
      setDiagramWidth(Math.round(nextWidth));
    };

    updateWidth(node.getBoundingClientRect().width);

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateWidth(entry.contentRect.width);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!showSensorPanel) return;

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeEditor();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showSensorPanel, closeEditor]);

  useEffect(() => {
    onSensorEditOpenChange?.(showSensorPanel);
  }, [showSensorPanel, onSensorEditOpenChange]);

  useEffect(() => {
    if (!tree?.id) {
      setCareEntries([]);
      return;
    }
    if (!loadCareData) {
      setCareEntries([]);
      return;
    }
    const list =
      tree?.tasks && tree.tasks.length
        ? tree.tasks
        : Array.isArray(taskData)
        ? taskData
        : taskData?.results || taskData?.data || [];
    if (!list?.length) {
      setCareEntries([]);
      return;
    }
    const mapped = list
      .filter((task) => String(task.treeId || task.tree?.id) === String(tree.id))
      .filter((task) =>
        Boolean(task.measureType || task.title || task.costCenter || task.data?.sichtkontrolleMeta)
      )
      .map((task) => mapTaskToCareEntry(task));
    setCareEntries(mapped);
  }, [taskData, tree?.id, tree?.tasks, loadCareData]);

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    setShow3DViewer(false);
  }, [tree?.id]);

  const combinedEntries = useMemo(() => {
    const imageEntries = [];

    // Convert tree images to care entries
    if (treeImages && treeImages.length > 0) {
      treeImages.forEach((img) => {
        imageEntries.push(mapImageToCareEntry(img, "tree"));
      });
    }

    // Convert leaf images to care entries
    if (leafImages && leafImages.length > 0) {
      leafImages.forEach((img) => {
        imageEntries.push(mapImageToCareEntry(img, "leaf"));
      });
    }

    // Combine and sort by timestamp
    return [...careEntries, ...imageEntries].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [careEntries, treeImages, leafImages]);

  async function handleSaveEntry(entry) {
    const entryPayload = entry || {};
    if (!tree?.id) {
      const normalized = normalizeCareEntry(entryPayload);
      setCareEntries((prev) => [...prev, normalized]);
      const goesToHistory = ["ERLEDIGT", "NICHT_GESCHAFFT"].includes(normalized.status);
      setActiveTab(goesToHistory ? "history" : "tasks");
      setPendingScrollId(normalized.id);
      return;
    }

    try {
      const documentFile = getEntryDocumentFile(entryPayload);
      const payload = stripTaskPayloadDocumentFile(
        buildTaskPayloadFromEntry(entryPayload, tree.id)
      );
      const saved = await createTask(payload);
      let savedWithDocument = saved;
      if (documentFile && saved?.id) {
        try {
          savedWithDocument = await uploadTaskRegelkontrolleDocument(saved.id, documentFile);
        } catch (uploadError) {
          console.error("Failed to upload Regelkontrolle document", uploadError);
        }
      }
      const mapped = mapTaskToCareEntry(savedWithDocument);
      setCareEntries((prev) => [...prev, mapped]);
      queryClient.setQueryData(["tasks", tree.id], (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return [savedWithDocument, ...list];
      });
      queryClient.setQueryData("tasks", (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return [savedWithDocument, ...list];
      });
      const goesToHistory = ["ERLEDIGT", "NICHT_GESCHAFFT"].includes(mapped.status);
      setActiveTab(goesToHistory ? "history" : "tasks");
      setPendingScrollId(mapped.id);

      // Auto-create follow-up Regelkontrolle task if next inspection date is set
      const naechsteKontrolle = entryPayload.data?.sichtkontrolleMeta?.naechsteKontrolle;
      if (naechsteKontrolle && entryPayload.measureType === "Regelkontrolle") {
        try {
          const followUpEntry = {
            measureType: "Regelkontrolle",
            entryKind: "task",
            source: "task",
            timestamp: naechsteKontrolle,
            status: "GEPLANT",
            priority: entryPayload.priority || "MITTEL",
            assignedTo: entryPayload.assignedTo || "",
            assigneeId: entryPayload.assigneeId || undefined,
            data: {
              beschreibung: t("care_chat.follow_up_inspection"),
            },
          };
          const followUpPayload = buildTaskPayloadFromEntry(followUpEntry, tree.id);
          const followUpSaved = await createTask(followUpPayload);
          const followUpMapped = mapTaskToCareEntry(followUpSaved);
          setCareEntries((prev) => [...prev, followUpMapped]);
          queryClient.setQueryData(["tasks", tree.id], (prev) => {
            const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
            return [followUpSaved, ...list];
          });
          queryClient.setQueryData("tasks", (prev) => {
            const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
            return [followUpSaved, ...list];
          });
        } catch (followUpError) {
          console.error("Failed to create follow-up inspection task", followUpError);
        }
      }
    } catch (error) {
      console.error("Failed to save care entry", error);
    }
  }

  async function handleEntryUpdate(id, updates) {
    if (!id) return;
    const current = combinedEntries.find((entry) => entry.id === id);
    if (!current) return;
    if (!tree?.id) return;

    const taskPayload = buildTaskUpdateFromCare(current, updates);
    try {
      const saved = await updateTaskMutation.mutateAsync(taskPayload);
      const mapped = mapTaskToCareEntry(saved);
      setCareEntries((prev) => prev.map((entry) => (entry.id === id ? mapped : entry)));
      queryClient.setQueryData(["tasks", tree?.id], (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return list.map((task) =>
          String(task.id) === String(current.sourceId) ? { ...task, ...saved } : task
        );
      });
      queryClient.setQueryData("tasks", (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return list.map((task) =>
          String(task.id) === String(current.sourceId) ? { ...task, ...saved } : task
        );
      });
      const goesToHistory = ["ERLEDIGT", "NICHT_GESCHAFFT"].includes(mapped.status);
      if (goesToHistory) {
        setActiveTab("history");
        setPendingScrollId(mapped.id);
      }
    } catch (error) {
      console.error("Failed to update task from care view", error);
    }
  }

  async function handleEntryDelete(id) {
    if (!id) return;
    const current = combinedEntries.find((entry) => entry.id === id);
    setCareEntries((prev) => prev.filter((entry) => entry.id !== id));

    if (!tree?.id || !current?.sourceId) return;

    try {
      await deleteTaskMutation.mutateAsync(current.sourceId);
      queryClient.setQueryData(["tasks", tree?.id], (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return list.filter((task) => String(task.id) !== String(current.sourceId));
      });
      queryClient.setQueryData("tasks", (prev) => {
        const list = Array.isArray(prev) ? prev : prev?.results || prev?.data || [];
        return list.filter((task) => String(task.id) !== String(current.sourceId));
      });
    } catch (error) {
      console.error("Failed to delete task from care view", error);
    }
  }

  useEffect(() => {
    const now = new Date();
    const needsUpdate = careEntries.some(
      (entry) => TASK_STATUSES.includes(entry.status) && new Date(entry.timestamp) <= now
    );
    if (!needsUpdate) return;
    setCareEntries((prev) =>
      prev.map((entry) => {
        if (TASK_STATUSES.includes(entry.status) && new Date(entry.timestamp) <= now) {
          return { ...entry, status: "ERLEDIGT" };
        }
        return entry;
      })
    );
  }, [careEntries]);

  useEffect(() => {
    if (!pendingScrollId || (activeTab !== "tasks" && activeTab !== "history")) return;
    const target = document.getElementById(`care-entry-${pendingScrollId}`);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScrollId(null);
  }, [pendingScrollId, activeTab, careEntries]);

  function handlePlanInspection() {
    const target = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    setComposerPrefill({
      measureType: "Regelkontrolle",
      status: "GEPLANT",
      timestamp: target.toISOString(),
      beschreibung: "Kontrolle geplant",
    });
    setActiveTab("tasks");
    setComposerOpen(true);
  }

  function handleDoInspection() {
    const now = new Date();
    setComposerPrefill({
      measureType: "Regelkontrolle",
      status: "ERLEDIGT",
      timestamp: now.toISOString(),
      beschreibung: "Kontrolle durchgeführt",
    });
    setActiveTab("history");
    setComposerOpen(true);
  }
  const prevInstallOpenRef = useRef(false);

  useEffect(() => {
    if (prevInstallOpenRef.current && !sensorInstallOpen && tree?.id) {
      queryClient.invalidateQueries(["tree", tree.id]);
      queryClient.invalidateQueries("trees");
    }
    prevInstallOpenRef.current = sensorInstallOpen;
  }, [sensorInstallOpen, tree?.id, queryClient]);

  const treeDiagram = (
    <div className="tree-diagram-stage">
      <Tree
        tree={treeForDiagram}
        edit={edit}
        setEdit={setEdit}
        soilEdit={soilEdit}
        setSoilEdit={setSoilEdit}
        selectedInstallation={selectedInstallation}
        setSelectedInstallation={handleSelectInstallation}
        showData={showData}
        setShowData={setShowData}
        previewHeight={sensorHeight ?? defaultHeight}
        previewDepth={sensorDepth}
        previewSensorType={showInstallPreview ? installSensorType : null}
        onPreviewHeightChange={
          showInstallPreview && !isSoilInstallType ? handlePreviewHeightChange : undefined
        }
        onPreviewDepthChange={
          showInstallPreview && isSoilInstallType ? handlePreviewDepthChange : undefined
        }
        onPreviewInstall={undefined}
        allowPreviewDrag={showInstallPreview}
        hideInstallations={false}
        showSelectedSensorPanel={false}
        centerTree
        onOpenConfigPanel={canEditTree ? (scope) => openConfigPanel("install", scope) : undefined}
        hideEditButtons={isPublic || !canEditTree}
      />
    </div>
  );

  const handleOpen3DFilePicker = () => {
    if (!canManage3DModel || is3DModelMutating) return;
    modelFileInputRef.current?.click();
  };

  const handle3DFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !onUpload3DModel) return;
    try {
      await onUpload3DModel(file);
      setShow3DViewer(true);
    } catch (error) {
      console.error("Failed to upload 3D model", error);
    }
  };

  const handleRemove3DModel = async () => {
    if (!canManage3DModel || !onRemove3DModel || is3DModelMutating) return;
    try {
      await onRemove3DModel();
      setShow3DViewer(false);
    } catch (error) {
      console.error("Failed to remove 3D model", error);
    }
  };

  return (
    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
      <div
        className="tree-detail-shell"
        style={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}
      >
        <div className="tree-detail-tabs-header entity-detail-header">
          <div className="d-flex align-items-center justify-content-between gap-2 w-100 entity-detail-header-row">
            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
              {onClose && (
                <button
                  type="button"
                  className="tree-detail-close-btn entity-detail-control"
                  onClick={onClose}
                  aria-label={t("close")}
                  title={t("close")}
                >
                  <CloseIcon />
                </button>
              )}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <Nav variant="tabs" className="tree-detail-tabs mb-0">
                  <Nav.Item>
                    <Nav.Link eventKey="info" className="d-flex align-items-center gap-2">
                      <ClipboardDataIcon size="1em" />
                      <span className="tree-detail-tab-title text-truncate" title={infoTabLabel}>
                        {infoTabLabel}
                      </span>
                    </Nav.Link>
                  </Nav.Item>
                  {!isPublic && (
                    <Nav.Item>
                      <Nav.Link eventKey="history">
                        <ClockIcon size="1em" />
                        <span className="tree-detail-tab-label">{t("history")}</span>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {!isPublic && (
                    <Nav.Item>
                      <Nav.Link eventKey="tasks">
                        <WrenchIcon size="1em" />
                        <span className="tree-detail-tab-label">{t("tasks")}</span>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </div>
            </div>
            {headerActions && (
              <div className="tree-detail-header-actions d-flex align-items-center gap-2">
                {headerActions}
              </div>
            )}
          </div>
        </div>

        <Tab.Content
          className="pt-3 tree-detail-tab-content entity-detail-body"
          style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
        >
          <Tab.Pane eventKey="info" className="tree-detail-tab-pane" style={{ height: "100%" }}>
            <div className="d-flex flex-column gap-4 mb-4" style={{ width: "100%" }}>
              <div
                ref={diagramRef}
                className={`tree-diagram-shell position-relative ${
                  showSensorPanel ? "is-editor-open" : ""
                }`}
              >
                <input
                  ref={modelFileInputRef}
                  type="file"
                  accept=".glb,model/gltf-binary"
                  className="d-none"
                  onChange={handle3DFileChange}
                />
                {!showSensorPanel && (
                  <>
                    {(has3DModel || canManage3DModel) && (
                      <div className="tree-detail-model-controls">
                        {has3DModel && (
                          <button
                            type="button"
                            className="tree-detail-view-toggle"
                            onClick={() => setShow3DViewer((prev) => !prev)}
                            aria-pressed={show3DViewer}
                            title={show3DViewer ? "Switch to tree view" : "Switch to 3D view"}
                          >
                            <span className="tree-detail-view-toggle__icon">
                              {show3DViewer ? "2D" : "3D"}
                            </span>
                          </button>
                        )}
                        {canManage3DModel && (
                          <div className="tree-detail-model-admin-actions">
                            <Button
                              type="button"
                              size="sm"
                              variant="light"
                              className="tree-detail-model-action-btn"
                              onClick={handleOpen3DFilePicker}
                              disabled={is3DModelMutating}
                            >
                              {has3DModel
                                ? t("replace_3d_model", "Replace 3D model")
                                : t("upload_3d_model", "Upload 3D model")}
                            </Button>
                            {has3DModel && (
                              <Button
                                type="button"
                                size="sm"
                                variant="light"
                                className="tree-detail-model-action-btn tree-detail-model-action-btn--danger"
                                onClick={handleRemove3DModel}
                                disabled={is3DModelMutating}
                              >
                                {t("remove_3d_model", "Remove 3D model")}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {show3DViewer ? <TreeModelViewer tree={tree} t={t} /> : treeDiagram}
                  </>
                )}
                {showSensorPanel && (
                  <div className={`tree-editor-split ${isStackedEditorLayout ? "is-stacked" : ""}`}>
                    <div className="tree-editor-treePane">{treeDiagram}</div>
                    <div className="tree-editor-editorPane">
                      <Card className="tree-editor-panel">
                        <Card.Body className="tree-editor-drawer-content p-0">
                          <div className="tree-editor-drawer-header">
                            {showSensorDetails ? (
                              <>
                                <button
                                  type="button"
                                  className="tree-detail-close-btn entity-detail-control"
                                  onClick={closeEditor}
                                  aria-label={t("close")}
                                  title={t("close")}
                                >
                                  <CloseIcon />
                                </button>
                                <div className="fw-semibold text-truncate flex-grow-1">
                                  {`${t("sensor")}: ${selectedInstallation?.sensor?.name || "-"}`}
                                </div>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as="button"
                                    className="tree-detail-icon-link tree-detail-actions-toggle entity-detail-control entity-detail-actions-toggle border-0"
                                    id="tree-installation-actions-dropdown"
                                    title={t("more_actions", "Mehr Aktionen")}
                                  >
                                    <MoreIcon size="24px" />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    {canEditTree && (
                                      <>
                                        <Dropdown.Item onClick={handleEditInstallation}>
                                          <EditIcon size="16px" /> {t("edit", "Bearbeiten")}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={handleRemoveInstallation}
                                          disabled={removeInstallationMutation.isLoading}
                                          className="text-danger"
                                        >
                                          <TrashIcon size="16px" /> {t("delete", "Löschen")}
                                        </Dropdown.Item>
                                      </>
                                    )}
                                    <Dropdown.Item onClick={handleOpenInstallationInfo}>
                                      <InfoIcon size="16px" />{" "}
                                      {t("more_information", "Mehr Informationen")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </>
                            ) : (
                              <>
                                <div className="fw-semibold">
                                  {configTab === "thresholds"
                                    ? thresholdTabLabel
                                    : t("install_sensor")}
                                </div>
                                <Button variant="outline-secondary" size="sm" onClick={closeEditor}>
                                  {t("cancel")}
                                </Button>
                              </>
                            )}
                          </div>
                          <div className="tree-editor-drawer-body">
                            {showSensorDetails ? (
                              <>
                                <Nav
                                  variant="tabs"
                                  activeKey={sensorDetailTab}
                                  onSelect={(key) => setSensorDetailTab(key || "overview")}
                                  className="mb-3 tree-editor-config-tabs"
                                >
                                  <Nav.Item>
                                    <Nav.Link eventKey="overview">
                                      {sensorOverviewTabLabel}
                                    </Nav.Link>
                                  </Nav.Item>
                                  <Nav.Item>
                                    <Nav.Link eventKey="measurements">
                                      {t("last_10_measurements")}
                                    </Nav.Link>
                                  </Nav.Item>
                                </Nav>
                                {sensorDetailTab === "measurements" ? (
                                  <>
                                    <div className="small text-muted mb-3">
                                      {selectedInstallation?.installed_at ? (
                                        <>
                                          {t("installed_at")}: {selectedInstallation.installed_at}
                                        </>
                                      ) : (
                                        t("installed_at")
                                      )}
                                      {selectedInstallation?.removed_at ? (
                                        <>
                                          {" • "}
                                          {t("removed_at")}: {selectedInstallation.removed_at}
                                        </>
                                      ) : null}
                                    </div>
                                    {isLoadingInstallationMeasurements ? (
                                      <div className="d-flex justify-content-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                          <span className="visually-hidden">{t("loading")}</span>
                                        </div>
                                      </div>
                                    ) : installationMeasurements.length > 0 ? (
                                      <SensorMeasurementsTable
                                        rows={installationMeasurements}
                                        sensorType={installationMeasurementSensorType}
                                        valueKey={installationMeasurementValueKey}
                                      />
                                    ) : (
                                      <div className="text-center py-4">
                                        <p className="mb-1 fw-semibold">
                                          {t("sensor_details.no_recent_measurements")}
                                        </p>
                                        <small className="text-muted">
                                          {t("sensor_details.no_recent_measurements_hint")}
                                        </small>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div
                                      className={
                                        showInstallationInfo
                                          ? "tree-sensor-details-grid"
                                          : "tree-sensor-details-gauge"
                                      }
                                    >
                                      <div className="tree-sensor-details-gauge">
                                        {selectedInstallation &&
                                        getHealthStateCrown(selectedInstallation.sensor) !== 3 ? (
                                          <MoistureGauge
                                            key={`moisture-gauge-${selectedInstallation.id}-${editorLayoutKey}`}
                                            tree={tree}
                                            sensor={selectedInstallation.sensor}
                                            t={t}
                                            soilWaterTensionType={soilWaterTensionType}
                                            setSoilWaterTensionType={setSoilWaterTensionType}
                                            layoutKey={editorLayoutKey}
                                            layoutWidth={diagramWidth}
                                            calibrationSpeciesName={null}
                                          />
                                        ) : (
                                          <div className="text-muted small">
                                            Keine Live-Daten verf&#252;gbar.
                                          </div>
                                        )}
                                      </div>
                                      {showInstallationInfo && (
                                        <div className="tree-sensor-details-info">
                                          <div className="d-flex justify-content-end mb-2">
                                            <button
                                              type="button"
                                              className="tree-detail-close-btn entity-detail-control"
                                              onClick={() => {
                                                setShowInstallationInfo(false);
                                                setIsEditingInstallationMeta(false);
                                              }}
                                              aria-label={t("close")}
                                              title={t("close")}
                                            >
                                              <CloseIcon />
                                            </button>
                                          </div>
                                          <div className="small mb-2">
                                            <strong>{t("meaning_measurement")}</strong>:{" "}
                                            {treeStateDescription(selectedInstallation?.sensor)}
                                          </div>
                                          {isEditingInstallationMeta ? (
                                            <>
                                              <div className="mb-2">
                                                <label className="form-label form-label-sm mb-1">
                                                  {t("installed_at")}
                                                </label>
                                                <input
                                                  type="date"
                                                  className="form-control form-control-sm"
                                                  value={installationMetaForm.installed_at}
                                                  onChange={(event) =>
                                                    setInstallationMetaForm((prev) => ({
                                                      ...prev,
                                                      installed_at: event.target.value,
                                                    }))
                                                  }
                                                />
                                              </div>
                                              <div className="mb-2">
                                                <label className="form-label form-label-sm mb-1">
                                                  {t("removed_at")}
                                                </label>
                                                <input
                                                  type="date"
                                                  className="form-control form-control-sm"
                                                  value={installationMetaForm.removed_at}
                                                  onChange={(event) =>
                                                    setInstallationMetaForm((prev) => ({
                                                      ...prev,
                                                      removed_at: event.target.value,
                                                    }))
                                                  }
                                                />
                                              </div>
                                              {isSelectedInstallationSoilType ? (
                                                <div className="mb-2">
                                                  <label className="form-label form-label-sm mb-1">
                                                    {t("depth")}
                                                  </label>
                                                  <select
                                                    className="form-select form-select-sm"
                                                    value={installationMetaForm.depth || ""}
                                                    onChange={(event) =>
                                                      setInstallationMetaForm((prev) => ({
                                                        ...prev,
                                                        depth: event.target.value,
                                                      }))
                                                    }
                                                  >
                                                    <option value="">{t("select_depth")}</option>
                                                    <option value="0">
                                                      {t("root_ball", { defaultValue: "Ballen" })}
                                                    </option>
                                                    <option value="30">30 cm</option>
                                                    <option value="60">60 cm</option>
                                                    <option value="90">90 cm</option>
                                                    <option value="120">120 cm</option>
                                                  </select>
                                                </div>
                                              ) : (
                                                <div className="mb-2">
                                                  <label className="form-label form-label-sm mb-1">
                                                    {t("height")} (m)
                                                  </label>
                                                  <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    min="1"
                                                    step="1"
                                                    value={installationMetaForm.height}
                                                    onChange={(event) =>
                                                      setInstallationMetaForm((prev) => ({
                                                        ...prev,
                                                        height: event.target.value,
                                                      }))
                                                    }
                                                  />
                                                </div>
                                              )}
                                              <div className="d-flex justify-content-end gap-2">
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  onClick={() =>
                                                    setIsEditingInstallationMeta(false)
                                                  }
                                                >
                                                  {t("cancel")}
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="primary"
                                                  onClick={handleSaveInstallationMeta}
                                                  disabled={
                                                    updateInstallationMetaMutation.isLoading
                                                  }
                                                >
                                                  {updateInstallationMetaMutation.isLoading
                                                    ? t("saving")
                                                    : t("save")}
                                                </Button>
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              <div className="small mb-2">
                                                <strong>{t("installed_at")}</strong>:{" "}
                                                {selectedInstallation?.installed_at ||
                                                  t("not_provided")}
                                              </div>
                                              <div className="small mb-2">
                                                <strong>{t("removed_at")}</strong>:{" "}
                                                {selectedInstallation?.removed_at ||
                                                  t("not_provided")}
                                              </div>
                                            </>
                                          )}
                                          {getSensorType(selectedInstallation?.sensor) ===
                                            "pulse" && (
                                            <div className="small mt-2">
                                              <strong>{t("calibration_species")}</strong>:{" "}
                                              {calibrationSpeciesName || t("not_provided")}
                                            </div>
                                          )}
                                          <div className="small mt-2">
                                            <strong>
                                              {selectedInstallation?.sensor?.network_type === "NB"
                                                ? t("imei")
                                                : t("dev_eui")}
                                            </strong>
                                            :{" "}
                                            {selectedInstallation?.sensor?.network_type === "NB"
                                              ? selectedInstallation?.sensor?.imei
                                              : selectedInstallation?.sensor?.hardware_serial}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <Nav
                                  variant="tabs"
                                  activeKey={configTab}
                                  onSelect={(key) => setConfigTab(key || "install")}
                                  className="mb-3 tree-editor-config-tabs"
                                >
                                  <Nav.Item>
                                    <Nav.Link eventKey="install">{t("install")}</Nav.Link>
                                  </Nav.Item>
                                  <Nav.Item>
                                    <Nav.Link eventKey="thresholds">{t("thresholds")}</Nav.Link>
                                  </Nav.Item>
                                </Nav>
                                {configTab === "install" ? (
                                  <div className="tree-editor-install">
                                    {configScope === "crown" && (
                                      <>
                                        <p className="text-muted small mb-3">
                                          {t("sesnor_height_hint")}
                                        </p>
                                        <label className="form-label mb-1 small">
                                          {t("height")} (m)
                                        </label>
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          className="form-control form-control-sm mb-3"
                                          value={sensorHeightInput}
                                          onChange={(event) =>
                                            setSensorHeightInput(event.target.value)
                                          }
                                          onBlur={() => commitHeight(sensorHeightInput)}
                                          onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                              event.preventDefault();
                                              commitHeight(sensorHeightInput);
                                            }
                                          }}
                                        />
                                      </>
                                    )}
                                    <InstallationForm
                                      show={sensorInstallOpen}
                                      renderMode="inline"
                                      tId={tree?.id}
                                      setOpenForm={(isOpen) => {
                                        if (isOpen) {
                                          setSensorInstallOpen(true);
                                          return;
                                        }
                                        closeEditor();
                                      }}
                                      isUpdate={installIsUpdate}
                                      setIsUpdate={setInstallIsUpdate}
                                      installationItem={installationItem}
                                      setInstallationItem={setInstallationItem}
                                      currentInstallations={activeInstallations}
                                      prefillHeight={sensorHeight ?? defaultHeight}
                                      externalHeight={sensorHeight ?? defaultHeight}
                                      hideHeightField={configScope === "crown"}
                                      externalDepth={sensorDepth}
                                      onDepthChange={handlePreviewDepthChange}
                                      forceSoilMode={configScope === "soil"}
                                      onSensorTypeChange={setInstallSensorType}
                                    />
                                  </div>
                                ) : (
                                  <div className="tree-editor-thresholds">
                                    {configScope === "soil" ? (
                                      <SoilThresholdSlider tree={tree} setEdit={closeEditor} />
                                    ) : (
                                      <HealthThresholdSlider tree={tree} setEdit={closeEditor} />
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <TreeInfo
              tree={tree}
              infoAction={<InfoBox infoTitle={t("tree_info")} infoContent={treeInfoHelpContent} />}
              isPublic={isPublic}
            />
            {showLocationMap && hasTreePosition && (
              <Card className="tree-detail-location-card">
                <Card.Body>
                  <div className="tree-detail-location-card__header">
                    <MapMarkerAltIcon width="16px" />
                    <span>{t("location")}</span>
                  </div>
                  <div className="tree-detail-location-card__map">
                    <MapContainer
                      center={[treeLat, treeLng]}
                      zoom={16}
                      scrollWheelZoom={false}
                      style={{ height: "100%", width: "100%" }}
                      dragging={false}
                      doubleClickZoom={false}
                      zoomControl={false}
                      attributionControl={false}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker
                        position={[treeLat, treeLng]}
                        icon={parentTreeIcon(tree.health_state)}
                      />
                    </MapContainer>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="history" className="tree-detail-tab-pane" style={{ height: "100%" }}>
            <TreeDetailCareChat
              tree={tree}
              mode="history"
              entries={combinedEntries}
              onEntriesChange={setCareEntries}
              onEntryUpdate={handleEntryUpdate}
              onEntryDelete={handleEntryDelete}
            />
          </Tab.Pane>

          <Tab.Pane eventKey="tasks" className="tree-detail-tab-pane" style={{ height: "100%" }}>
            <TreeDetailCareChat
              tree={tree}
              mode="tasks"
              entries={combinedEntries}
              onEntriesChange={setCareEntries}
              onEntryUpdate={handleEntryUpdate}
              onEntryDelete={handleEntryDelete}
              onPlanInspection={handlePlanInspection}
              onDoInspection={handleDoInspection}
            />
          </Tab.Pane>
        </Tab.Content>

        {!isPublic && (
          <div
            className="tree-detail-composer-dock"
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              background: "var(--color-bg-canvas, #fff)",
            }}
          >
            <TreeDetailCareComposerInline
              onSave={handleSaveEntry}
              prefill={composerPrefill}
              onPrefillApplied={() => setComposerPrefill(null)}
              mode={activeTab}
              tree={tree}
              onAddImage={() => setOpenImageForm(true)}
              collapsed={!composerOpen}
              onToggleCollapsed={() => setComposerOpen((prev) => !prev)}
            />
          </div>
        )}
      </div>
    </Tab.Container>
  );
}

function PhotoGalleryTab({
  treeImages,
  leafImages,
  treeIndex,
  setTreeIndex,
  leafIndex,
  setLeafIndex,
  handleDeleteImage,
  setOpenImageForm,
  t,
}) {
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>{t("photos")}</h5>
        <Button size="sm" variant="primary" onClick={() => setOpenImageForm(true)}>
          <AddIcon size="1em" /> {t("add_photo")}
        </Button>
      </div>
      {treeImages.length > 0 && (
        <>
          <h5 className="mt-3 mb-2">
            {t("tree_photos")}{" "}
            <Badge bg="secondary">
              {treeIndex + 1} / {treeImages.length}
            </Badge>
          </h5>
          <Card className="mb-3">
            <Card.Img
              variant="top"
              src={treeImages[treeIndex]?.tree_image}
              style={{ maxHeight: "400px", objectFit: "contain" }}
            />
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={treeIndex === 0}
                    onClick={() => setTreeIndex(treeIndex - 1)}
                  >
                    <AngleLeftIcon />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={treeIndex >= treeImages.length - 1}
                    onClick={() => setTreeIndex(treeIndex + 1)}
                  >
                    <AngleRightIcon />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleDeleteImage(treeImages[treeIndex]?.id, false)}
                >
                  <TrashIcon size="1em" /> {t("delete")}
                </Button>
              </div>
            </Card.Body>
          </Card>
          <div
            className="photo-thumbnail-grid mt-3"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: "8px",
            }}
          >
            {treeImages.map((img, idx) => (
              <img
                key={idx}
                src={img.tree_image}
                alt={`Tree ${idx + 1}`}
                onClick={() => setTreeIndex(idx)}
                style={{
                  width: "100%",
                  height: "80px",
                  objectFit: "cover",
                  border: idx === treeIndex ? "3px solid #286CA5" : "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </>
      )}

      {leafImages.length > 0 && (
        <>
          <h5 className="mt-4 mb-2">
            {t("leaf_photos")}{" "}
            <Badge bg="secondary">
              {leafIndex + 1} / {leafImages.length}
            </Badge>
          </h5>
          <Card className="mb-3">
            <Card.Img
              variant="top"
              src={leafImages[leafIndex]?.leaf_image}
              style={{ maxHeight: "400px", objectFit: "contain" }}
            />
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={leafIndex === 0}
                    onClick={() => setLeafIndex(leafIndex - 1)}
                  >
                    <AngleLeftIcon />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={leafIndex >= leafImages.length - 1}
                    onClick={() => setLeafIndex(leafIndex + 1)}
                  >
                    <AngleRightIcon />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleDeleteImage(leafImages[leafIndex]?.id, true)}
                >
                  <TrashIcon size="1em" /> {t("delete")}
                </Button>
              </div>
            </Card.Body>
          </Card>
          <div
            className="photo-thumbnail-grid mt-3"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: "8px",
            }}
          >
            {leafImages.map((img, idx) => (
              <img
                key={idx}
                src={img.leaf_image}
                alt={`Leaf ${idx + 1}`}
                onClick={() => setLeafIndex(idx)}
                style={{
                  width: "100%",
                  height: "80px",
                  objectFit: "cover",
                  border: idx === leafIndex ? "3px solid #286CA5" : "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </>
      )}

      {treeImages.length === 0 && leafImages.length === 0 && (
        <Card>
          <Card.Body className="text-center text-muted">{t("no_photos")}</Card.Body>
        </Card>
      )}
    </div>
  );
}
